/**
 * Shared Session Timeout Utility
 * 
 * SINGLE SOURCE OF TRUTH for session timeout logic
 * Used by both tenant auth and platform admin auth
 * 
 * Following 00-READ-THIS-FIRST principles:
 * - SINGLE SOURCE OF TRUTH: One implementation for all session timeout logic
 * - NULL SAFETY: Explicit checks throughout
 * - NO TECH DEBT: Eliminates code duplication
 */

export interface SessionTimeoutConfig {
  idleTimeoutMinutes: number;
  absoluteTimeoutMinutes: number;
  storageKeyPrefix: string;
}

export interface SessionTimeoutState {
  lastActivityTime: Date | null;
  sessionStartTime: Date | null;
}

export interface SessionTimeoutCheck {
  isExpired: boolean;
  reason?: 'idle' | 'absolute' | 'token_missing' | 'token_expired';
  message?: string;
}

/**
 * Initialize session tracking
 * Call this after successful login
 */
export function initializeSession(config: SessionTimeoutConfig): SessionTimeoutState {
  const now = new Date();
  
  // Store session start time in localStorage
  localStorage.setItem(`${config.storageKeyPrefix}_session_start`, now.toISOString());
  
  return {
    lastActivityTime: now,
    sessionStartTime: now
  };
}

/**
 * Restore session state from localStorage
 * Call this on component mount to restore existing session
 */
export function restoreSessionState(
  config: SessionTimeoutConfig
): SessionTimeoutState {
  const storedStartTime = localStorage.getItem(`${config.storageKeyPrefix}_session_start`);
  const now = new Date();
  
  if (storedStartTime) {
    const parsedStartTime = new Date(storedStartTime);
    
    // Validate the date is valid
    if (!isNaN(parsedStartTime.getTime())) {
      // Check if stored session is beyond absolute timeout
      const minutesSinceStart = (now.getTime() - parsedStartTime.getTime()) / (1000 * 60);
      
      if (minutesSinceStart >= config.absoluteTimeoutMinutes) {
        // Session is stale, reset to now
        localStorage.setItem(`${config.storageKeyPrefix}_session_start`, now.toISOString());
        return {
          lastActivityTime: now,
          sessionStartTime: now
        };
      }
      
      // Valid stored session
      return {
        lastActivityTime: now,
        sessionStartTime: parsedStartTime
      };
    }
  }
  
  // No valid stored session, initialize new one
  localStorage.setItem(`${config.storageKeyPrefix}_session_start`, now.toISOString());
  return {
    lastActivityTime: now,
    sessionStartTime: now
  };
}

/**
 * Check if session has expired
 * Returns detailed information about expiration
 */
export function checkSessionTimeout(
  config: SessionTimeoutConfig,
  state: SessionTimeoutState,
  tokenStorageKey: string,
  isJWTExpiredFn?: (token: string) => boolean
): SessionTimeoutCheck {
  const token = localStorage.getItem(tokenStorageKey);
  
  // Check if token exists
  if (!token) {
    return {
      isExpired: true,
      reason: 'token_missing',
      message: 'No authentication token found'
    };
  }
  
  // Check JWT expiration if function provided
  if (isJWTExpiredFn && isJWTExpiredFn(token)) {
    return {
      isExpired: true,
      reason: 'token_expired',
      message: 'Token has expired'
    };
  }
  
  const now = new Date();
  
  // Check absolute timeout
  if (state.sessionStartTime) {
    const sessionDuration = (now.getTime() - state.sessionStartTime.getTime()) / (1000 * 60);
    if (sessionDuration >= config.absoluteTimeoutMinutes) {
      return {
        isExpired: true,
        reason: 'absolute',
        message: `Session expired due to maximum duration (${config.absoluteTimeoutMinutes / 60} hours)`
      };
    }
  }
  
  // Check idle timeout
  if (state.lastActivityTime) {
    const idleTime = (now.getTime() - state.lastActivityTime.getTime()) / (1000 * 60);
    if (idleTime >= config.idleTimeoutMinutes) {
      return {
        isExpired: true,
        reason: 'idle',
        message: `Session expired due to inactivity (${config.idleTimeoutMinutes} minutes)`
      };
    }
  }
  
  // Session is valid
  return {
    isExpired: false
  };
}

/**
 * Clear session data from localStorage
 * Call this on logout
 */
export function clearSessionData(config: SessionTimeoutConfig): void {
  localStorage.removeItem(`${config.storageKeyPrefix}_session_start`);
}

/**
 * Update last activity time
 * Call this on user interaction
 */
export function updateActivity(): Date {
  return new Date();
}

/**
 * Create activity event listeners
 * Returns cleanup function
 */
export function createActivityListeners(
  onActivity: () => void,
  events: string[] = ['mousedown', 'keypress', 'scroll', 'touchstart']
): () => void {
  // Add event listeners
  events.forEach(event => {
    window.addEventListener(event, onActivity, { passive: true });
  });
  
  // Return cleanup function
  return () => {
    events.forEach(event => {
      window.removeEventListener(event, onActivity);
    });
  };
}
